import { useEffect, useState } from 'react';
import { Save, RotateCcw } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import {
  Button, Card, CardHeader, Field, Input, LoadingBlock, PageHeader, Select, useToast,
} from '@/components/ui';

interface ConfigRow {
  id: string;
  key: string;
  value: string;
  notes: string | null;
  updated_at: string | null;
}

/**
 * Groups, so this reads as a settings screen rather than a spreadsheet.
 *
 * Anything not listed here still appears, under "Everything else" — a new
 * config key must never become invisible just because nobody updated this
 * file.
 */
const GROUPS: { title: string; hint: string; keys: string[] }[] = [
  {
    title: 'Talking to customers',
    hint: 'Where support, sales and legal mail lands, and whether live chat is offered inside PROVIA.',
    keys: ['support_email', 'contact_email', 'sales_email', 'billing_email', 'privacy_email',
      'legal_email', 'feedback_email', 'operations_email',
      'live_chat_enabled', 'live_chat_property_id', 'live_chat_widget_id'],
  },
  {
    title: 'The business',
    hint: 'What the product and the company are called, and the details the legal pages need.',
    keys: ['product_name', 'company_name', 'company_address', 'company_number', 'website_url'],
  },
  {
    title: 'Plans and pricing',
    hint: 'Shown on the public pricing page. Changing a price here does not change anybody’s existing subscription. '
      + 'Prices in other currencies are worked out from these — run syncPlanPrices() after changing one.',
    keys: ['price_starter_pence', 'price_standard_pence', 'starter_property_limit',
      'standard_property_limit', 'currency_symbol', 'base_currency', 'fx_rate_url',
      'trial_days', 'checkout_url_starter', 'checkout_url_standard'],
  },
  {
    title: 'Signing up',
    hint: 'The kill switch, and the demo login printed on the pricing page.',
    keys: ['signup_enabled', 'demo_email', 'demo_password'],
  },
  {
    title: 'Messaging',
    hint: 'Whether each organisation gets its own number, or everybody shares one.',
    keys: ['sms_number_policy'],
  },
  {
    title: 'Calling',
    hint: 'What the person you ring sees, and what you hear when you pick up. '
      + 'Your own number is set at the top of this page.',
    keys: ['calls_from_number', 'calls_announce', 'calls_timeout_seconds'],
  },
  {
    title: 'Lead outreach',
    hint: 'Who cold email leaves as, and whether a reply to that address reaches anybody.',
    keys: ['leads_from_address', 'leads_from_name', 'leads_reply_to',
      'leads_from_receives_replies', 'lead_outreach_batch_limit',
      'console_sender_addresses'],
  },
];

/** The handful that are a choice rather than free text. */
const CHOICES: Record<string, string[]> = {
  signup_enabled: ['true', 'false'],
  live_chat_enabled: ['true', 'false'],
  sms_number_policy: ['shared', 'per_org'],
  leads_from_receives_replies: ['true', 'false'],
};

/**
 * The phone a call from the console rings first.
 *
 * Kept at the top of this screen rather than buried with the deployment's
 * settings, because it is the one field on the page that belongs to *you* and
 * not to the deployment — two operators working the same list have two
 * different answers, and without it the Call button has nothing to ring.
 */
function YourPhone() {
  const toast = useToast();
  const { session, refresh } = useAuth();
  const mine = session?.kind === 'admin' ? session : null;
  const [value, setValue] = useState(mine?.mobile ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(mine?.mobile ?? ''); }, [mine?.mobile]);

  if (!mine) return null;
  const dirty = value.trim() !== (mine.mobile ?? '');

  async function save() {
    setSaving(true);
    try {
      await api.patch('/admin/me', { mobile: value.trim() });
      await refresh();
      toast.success(value.trim() ? 'Calls will ring that number' : 'Number removed');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That number could not be saved');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title="Your phone"
        subtitle="Pressing Call rings this number, then dials the other end once you answer"
      />
      <div className="p-5 sm:p-6 pt-4 grid sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
        <Field
          label="Your mobile"
          hint="In full international form — +447700900123, not 07700 900123. The same digits mean different countries, so it is refused rather than guessed at."
        >
          <Input
            value={value}
            placeholder="+447700900123"
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        <Button
          variant={dirty ? 'primary' : 'secondary'}
          disabled={!dirty}
          loading={saving}
          icon={<Save className="h-3.5 w-3.5" />}
          onClick={() => void save()}
        >
          Save
        </Button>
      </div>
    </Card>
  );
}

/**
 * The deployment's own settings.
 *
 * These used to be changed by opening the spreadsheet, which meant every price
 * change and every kill switch went through a tab nobody should have open
 * during business hours. Editing them here is audited and validated; they are
 * the same rows either way.
 */
export default function PlatformSettings() {
  const toast = useToast();
  const { data, loading, reload } = useApi<ConfigRow[]>('/admin/config');
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => { setEdited({}); }, [data]);

  const rows = data ?? [];
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const grouped = new Set(GROUPS.flatMap((g) => g.keys));
  const rest = rows.filter((r) => !grouped.has(r.key));

  async function save(key: string) {
    setSaving(key);
    try {
      await api.patch(`/admin/config/${key}`, { value: edited[key] ?? '' });
      toast.success(`${key} saved`);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save that setting');
    } finally {
      setSaving(null);
    }
  }

  function row(r: ConfigRow) {
    const value = edited[r.key] ?? r.value ?? '';
    const dirty = edited[r.key] !== undefined && edited[r.key] !== (r.value ?? '');
    const choices = CHOICES[r.key];

    return (
      <div key={r.id} className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
        <Field
          label={r.key}
          hint={r.notes ?? undefined}
        >
          {choices ? (
            <Select
              value={value}
              onChange={(e) => setEdited((m) => ({ ...m, [r.key]: e.target.value }))}
            >
              {choices.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          ) : (
            <Input
              value={value}
              onChange={(e) => setEdited((m) => ({ ...m, [r.key]: e.target.value }))}
            />
          )}
        </Field>
        <div className="flex gap-2">
          {dirty && (
            <Button
              size="sm"
              variant="ghost"
              icon={<RotateCcw className="h-3.5 w-3.5" />}
              onClick={() => setEdited((m) => {
                const next = { ...m };
                delete next[r.key];
                return next;
              })}
            >
              Undo
            </Button>
          )}
          <Button
            size="sm"
            variant={dirty ? 'primary' : 'secondary'}
            disabled={!dirty}
            loading={saving === r.key}
            icon={<Save className="h-3.5 w-3.5" />}
            onClick={() => void save(r.key)}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }

  if (loading && !data) return <LoadingBlock rows={6} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        subtitle="What this deployment says about itself. Saved one at a time, and every change is on the record."
      />

      <YourPhone />

      {GROUPS.map((group) => {
        const present = group.keys.map((k) => byKey.get(k)).filter(Boolean) as ConfigRow[];
        if (present.length === 0) return null;
        return (
          <Card key={group.title}>
            <CardHeader title={group.title} subtitle={group.hint} />
            <div className="p-5 sm:p-6 pt-4 space-y-4">{present.map(row)}</div>
          </Card>
        );
      })}

      {rest.length > 0 && (
        <Card>
          <CardHeader
            title="Everything else"
            subtitle="Settings this screen has no opinion about yet"
          />
          <div className="p-5 sm:p-6 pt-4 space-y-4">{rest.map(row)}</div>
        </Card>
      )}

      {rows.length > 0 && (
        <p className="text-xs text-faint">
          Last change {dateTime(
            rows.map((r) => r.updated_at ?? '').sort().pop() || new Date().toISOString(),
          )}.
        </p>
      )}
    </div>
  );
}
